/**
 * Règles de navigation de la console, sans React : quels onglets un compte
 * voit, quel onglet correspond à une adresse, où mène une section.
 * Partagées par le menu, la page à onglets et la table des routes, pour
 * qu'un onglet visible soit toujours un onglet atteignable.
 */
import { UserRole } from '../../types/rbac';
import type { Onglet, Section } from '../../config/sections';

/** Vrai si le compte a l'un des rôles demandés (liste absente : tous). */
export type VerifieRole = (roles: UserRole[]) => boolean;

/** Vrai si l'élément est ouvert au compte : sans `roles`, il l'est à tous. */
export const estAutorise = (roles: UserRole[] | undefined, aLeRole: VerifieRole): boolean =>
  !roles || roles.length === 0 || aLeRole(roles);

/** Assemble des segments d'adresse en un chemin absolu, sans « / » en double. */
export const cheminComplet = (...segments: string[]): string => {
  const morceaux = segments.flatMap((segment) => segment.split('/')).filter(Boolean);
  return `/${morceaux.join('/')}`;
};

/** Onglets ouverts au compte, dans l'ordre déclaré. */
export const ongletsVisibles = (onglets: Onglet[], aLeRole: VerifieRole): Onglet[] =>
  onglets.filter((onglet) => estAutorise(onglet.roles, aLeRole));

/**
 * Onglet de `onglets` dont le chemin est le plus long préfixe de `adresse`
 * (sous `base`) ; à défaut, le premier. Le plus long préfixe départage un
 * onglet racine (chemin vide) d'un onglet frère.
 */
export const ongletActif = (onglets: Onglet[], base: string, adresse: string): Onglet | undefined => {
  const correspond = (onglet: Onglet) => {
    const chemin = cheminComplet(base, onglet.chemin);
    return adresse === chemin || adresse.startsWith(`${chemin}/`);
  };
  const candidats = onglets.filter(correspond).sort((a, b) => b.chemin.length - a.chemin.length);
  return candidats[0] ?? onglets[0];
};

/**
 * Adresse de la première feuille ouverte au compte sous `onglets` (en
 * descendant dans les sous-onglets), ou `null` s'il n'en voit aucune.
 */
export const premiereFeuilleAutorisee = (
  base: string, onglets: Onglet[], aLeRole: VerifieRole,
): string | null => {
  for (const onglet of ongletsVisibles(onglets, aLeRole)) {
    const chemin = cheminComplet(base, onglet.chemin);
    if (!onglet.onglets) return chemin;
    const feuille = premiereFeuilleAutorisee(chemin, onglet.onglets, aLeRole);
    if (feuille) return feuille;
  }
  return null;
};

/** Vrai si la section a au moins une feuille ouverte au compte. */
export const sectionVisible = (section: Section, aLeRole: VerifieRole): boolean =>
  estAutorise(section.roles, aLeRole) && premiereFeuilleAutorisee(section.chemin, section.onglets, aLeRole) !== null;
